import epaxios from "@/utils/epaxios";
import RootStore from "../RootStore";
import { action, computed, makeObservable, observable } from "mobx";
import * as Sentry from '@sentry/browser';
import { permissions } from "@/constants";
import { UploadEditedReportPayloadDto, PublishReportPayloadDto } from "@/comp/pages/ReportsTabPage/types";

export default class ReportStore {
  rootStore: typeof RootStore;
  @observable isLoadingGenerateDeletePublishReport = false; 
  @observable isLoadingGetReports = false;
  @observable reports: {
    count?: number;
    currentPage?: number;
    data?;
    pageSize?: number;
    totalPages?: number;
  } = {};
  @observable pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  @observable latestPublishedReport = null;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action generateReport = async (payload) => {
    try {
      this.isLoadingGenerateDeletePublishReport = true;
      const stationId = this.rootStore.stationStore.currentStation.id;
      const { data } = await epaxios.post(`document-service/report-generator/station/${stationId}/generate-report`, payload);
      return data;
    } catch (error) {
      Sentry.captureException(new Error(`Error when generating report: ${error}`));
      throw error;
    } finally {
      this.isLoadingGenerateDeletePublishReport = false;
    }
  }

  @action getReports = async (page: number, pageSize: number, stationId?: string) => {
    try {
      this.isLoadingGetReports = true;
      const stationIdToUse = stationId || this.rootStore.stationStore.currentStation?.id;
      page = page || 1;
      pageSize = pageSize || 10;
      if (!stationIdToUse) {
        this.reports = {
          data: [],
          count: 0,
          currentPage: page,
          pageSize: pageSize,
          totalPages: 0,
        };
        return this.reports;
      }
      let url = `document-service/report-generator/station/${stationIdToUse}/reports?page=${page}&limit=${pageSize}`;
      if(this.canOnlyViewPeriodicReports && !this.canGenerateReport) {
        url += '&reportStatus=Published';
      }
      const { data } = await epaxios.get(url);
      
      if (data) {
        const parsedData = JSON.parse(JSON.stringify(data));
        
        this.reports = {
          data: parsedData?.data || [],
          count: parsedData?.count || 0,
          currentPage: parsedData?.currentPage || page,
          pageSize: parsedData?.pageSize || pageSize,
          totalPages: parsedData?.totalPages || 0,
        };

        this.pagination = {
          page: parsedData?.currentPage || page,
          pageSize: parsedData?.pageSize || pageSize,
          total: parsedData?.count || 0,
        };
      } else {
        this.reports = {
          data: [],
          count: 0,
          currentPage: page,
          pageSize: pageSize,
          totalPages: 0,
        };
      }
      
      return this.reports;
    } catch (error) {
      Sentry.captureException(new Error(`Error when getting reports: ${error}`));
      this.reports = {
        data: [],
        count: 0,
        currentPage: 1,
        pageSize: pageSize,
        totalPages: 0,
      };
      return this.reports;
    } finally {
      this.isLoadingGetReports = false;
    }
  }

  @action deleteReport = async (reportId: string) => {
    try {
      this.isLoadingGenerateDeletePublishReport = true;
      const stationId = this.rootStore.stationStore.currentStation?.id;
      const { data } = await epaxios.delete(`document-service/report-generator/station/${stationId}/report/${reportId}`);
      return data;
    } catch (error) {
      Sentry.captureException(new Error(`Error when deleting report: ${error}`));
      throw error;
    } finally {
      this.isLoadingGenerateDeletePublishReport = false;
    }
  }

  @action publishReport = async (reportId: string, payload: PublishReportPayloadDto) => {
    try {
      this.isLoadingGenerateDeletePublishReport = true;
      const stationId = this.rootStore.stationStore.currentStation?.id;
      const { data } = await epaxios.post(`document-service/report-generator/station/${stationId}/report/${reportId}/publish`, payload);
      return data;
    } catch (error) {
      Sentry.captureException(new Error(`Error when publishing report: ${error}`));
      throw error;
    } finally {
      this.isLoadingGenerateDeletePublishReport = false;
    }
  }

  @action getReportById = async (reportId: string) => {
    try {
      this.isLoadingGetReports = true;
      const stationId = this.rootStore.stationStore.currentStation?.id;
      const { data } = await epaxios.get(`document-service/report-generator/station/${stationId}/report/${reportId}`);
      return data;
    } catch (error) {
      Sentry.captureException(new Error(`Error when getting report by id: ${error}`));
      throw error;
    } finally {
      this.isLoadingGetReports = false;
    }
  }

  @action uploadEditedReport = async (reportId: string, file: File, payload: UploadEditedReportPayloadDto) => {  
    try {
      this.isLoadingGenerateDeletePublishReport = true;
      const stationId = this.rootStore.stationStore.currentStation?.id;
      
      const uploadToS3Response = await this.putEditedReportToS3(file, payload, stationId);
      console.log('uploadToS3Response', uploadToS3Response);
      //payload.reportName = uploadToS3Response?.asset?.filename;
      payload.s3Key = uploadToS3Response?.asset?.s3Key;
      const { data } = await epaxios.put(
        `document-service/report-generator/station/${stationId}/report/${reportId}/upload`,
        payload
      );
      return data;
    } catch (error) {
      Sentry.captureException(new Error(`Error when uploading edited report: ${error}`));
      throw error;
    } finally {
      this.isLoadingGenerateDeletePublishReport = false;
    }
  }

  @action putEditedReportToS3 = async (file: File, payload: UploadEditedReportPayloadDto, stationId: string) => {
    try {
      console.log('payload', payload);
      console.log('file', file);
      console.log('stationId', file.name);
      console.log('payload.description', file.type);

      const signedUrlResponse = await epaxios.post<{ url: string; filename: string; s3Key: string }>(
        `document-service/report-generator/station/${stationId}/generate-temporary-signed-urls`,
        {
          fileName: file.name,
          contentType: file.type,
          description: payload?.description || '',
        }
      );

      const { url: signedUrl, filename, s3Key } = signedUrlResponse.data;
      console.log('signedUrl', signedUrl);
      console.log('filename', filename);
      console.log('s3Key', s3Key);
      
      const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        throw new Error(`S3 upload failed with status: ${response.status}`);
      }

      // Step 3: Return the asset info with S3 details
      return {
        asset: {
          filename,
          s3Key,
        },
      };
    } catch (error) {
      Sentry.captureException(new Error(`Error when putting edited report to S3: ${error}`));
      throw error;
    } 
  }

  @action downloadReport = async (reportId: string , version?: string) => {
    try {
      this.isLoadingGenerateDeletePublishReport = true;
      const stationId = this.rootStore.stationStore.currentStation?.id;
      const queryParams = version ? `?version=${version}` : '';
      const { data } = await epaxios.get(`document-service/report-generator/station/${stationId}/report/${reportId}/download${queryParams}`);
      return data;
    } catch (error) {
      Sentry.captureException(new Error(`Error when downloading report: ${error}`));
      throw error;
    } finally {
      this.isLoadingGenerateDeletePublishReport = false;
    }
  }

  @action getLatestPublishedReport = async () => {
    try {
      this.isLoadingGenerateDeletePublishReport = true;
      const stationId = this.rootStore.stationStore.currentStation?.id;
      const { data } = await epaxios.get(`document-service/report-generator/station/${stationId}/latest-published-report`);
      this.latestPublishedReport = JSON.parse(JSON.stringify(data));
      return this.latestPublishedReport;
    } catch (error) {
      Sentry.captureException(new Error(`Error when getting latest published report: ${error}`));
      throw error;
    } finally {
      this.isLoadingGenerateDeletePublishReport = false;
    }
  }

  @computed get canGenerateReport() {
    return this.rootStore.permissionStore.isAuth(permissions.GENERATE_PERIODIC_REPORT) === true;
  }

  @computed get canOnlyViewPeriodicReports() {
    return this.rootStore.permissionStore.isAuth(permissions.VIEW_PERIODIC_REPORTS) === true;
  }

  
}